import { LitElement, html, nothing, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyFormMode = "client" | "server";
export type SkyFormMethod = "get" | "post";
export type SkyFormEnctype =
  | "application/x-www-form-urlencoded"
  | "multipart/form-data"
  | "text/plain";
export type SkyFormAutocomplete = "on" | "off";
export type SkyFormValues = Record<string, FormDataEntryValue | FormDataEntryValue[]>;
export type SkyFormChangeDetail = { values: SkyFormValues; formData: FormData; success: boolean };
export type SkyFormValidationErrorsDetail = { errors: string[] };
export type SkyFormSubmitDetail = { values: SkyFormValues; formData: FormData; success: boolean };
export type SkyFormFormDataFallbackDetail = { formData: FormData };

/**
 * @element sky-form
 *
 * @summary Form wrapper with unified validation, error aggregation, and client/server submit flows.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/form
 *
 * @slot - Form elements: inputs, buttons, and other form controls.
 *
 * @property {boolean} liveValidation - Validates and emits `form-change` on value changes.
 * @property {boolean} showErrors - Shows validation errors in child controls when available.
 * @property {boolean} errorBox - Shows consolidated validation messages above the form.
 * @property {string} errorBoxMessage - Header message shown in the consolidated error box.
 * @property {SkyFormMode} mode - Submission mode (`client` or `server`).
 * @property {SkyFormMethod | undefined} method - Native form method in server mode.
 * @property {string | undefined} action - Native form action in server mode.
 * @property {string | undefined} target - Native form target in server mode.
 * @property {SkyFormEnctype | undefined} enctype - Native form encoding type.
 * @property {SkyFormAutocomplete | undefined} autocomplete - Native form autocomplete behavior.
 * @property {boolean} noValidate - Disables native HTML constraint UI.
 * @property {boolean} enterSubmit - Submits on Enter for non-textarea controls.
 * @property {boolean} resetOnSubmit - Resets form after successful submit.
 * @property {string | null} csrfToken - Optional CSRF token value for app-level submit handling.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @fires {CustomEvent<SkyFormChangeDetail>} form-change - Fired when form values change in live validation mode.
 * @fires {CustomEvent<SkyFormValidationErrorsDetail>} validation-errors - Fired when aggregated validation errors change.
 * @fires {CustomEvent<SkyFormFormDataFallbackDetail> | FormDataEvent} formdata - Fired before client-mode submit with form data.
 * @fires {CustomEvent<SkyFormSubmitDetail>} form-submit - Fired after successful client-mode submit handling.
 *
 * @method checkValidity Returns native form validity without UI reporting.
 * @method reportValidity Returns native form validity and reports native UI messages.
 * @method requestSubmit Requests submission using optional submitter.
 * @method reset Resets native form and values.
 * @method resetForm Alias for `reset`.
 * @method submit Submits form using mode-specific client/server flow.
 *
 * @csspart error-box - Container for the consolidated error messages.
 * @csspart error-list - List element containing validation errors.
 *
 * @Behavior
 * - Provides seamless integration of custom form controls with native HTML form behavior
 * - Supports both client-side AJAX submissions and traditional server-side submissions
 * - Automatic validation aggregation from both native and custom form controls
 * - Light DOM rendering for proper browser form association
 *
 * The component automatically associates form controls (including custom elements) with the native form
 * using the `form` attribute, ensuring proper browser validation and FormData collection.
 *
 * - **Live Validation**: Validates on every input change (when liveValidation=true)
 * - **Submit Validation**: Validates only on form submission
 * - **Mixed Validation**: Combines both native HTML5 validation and custom component validation
 *
 * - **Client Mode**: Prevents page refresh, emits formdata/form-submit events for JavaScript handling
 * - **Server Mode**: Allows traditional form submission with page navigation/refresh
 *
 * @example
 * ```html
 * <sky-form id="login-form" live-validation error-box>
 *   <sky-input name="email" type="email" required label="Email"></sky-input>
 *   <sky-input name="password" type="password" required label="Password"></sky-input>
 *   <sky-button type="submit">Sign In</sky-button>
 * </sky-form>
 * ```
 * ```vue
 * <template>
 *   <sky-form id="login-form" live-validation error-box @form-submit="onSubmit">
 *     <sky-input name="email" type="email" required label="Email"></sky-input>
 *     <sky-input name="password" type="password" required label="Password"></sky-input>
 *     <sky-button type="submit">Sign In</sky-button>
 *   </sky-form>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-form id="login-form" live-validation error-box>
 *       <sky-input name="email" type="email" required label="Email"></sky-input>
 *       <sky-input name="password" type="password" required label="Password"></sky-input>
 *       <sky-button type="submit">Sign In</sky-button>
 *     </sky-form>
 *   );
 * }
 * ```
 */
@customElement("sky-form")
export class SkyForm extends LitElement {

  /**
   * When true, validates form on every value change.
   * @public
   */
  @property({ type: Boolean })
  liveValidation = false;

  /**
   * Controls whether validation errors are displayed.
   * @public
   */
  @property({ type: Boolean })
  showErrors = true;

  /**
   * When true, displays a consolidated error box with all validation errors.
   * @public
   */
  @property({ type: Boolean })
  errorBox = false;

  /**
   * Custom message displayed in the error box header.
   * @public
   */
  @property({ type: String })
  errorBoxMessage = "Fix These Issues Before Submitting";

  /**
   * Submission mode: 'client' prevents page refresh, 'server' allows native form submission.
   * @public
   */
  @property({ type: String })
  mode: SkyFormMode = "client";

  /**
   * HTTP method for form submission (when mode='server').
   * @public
   */
  @property({ type: String })
  method?: SkyFormMethod;

  /**
   * URL for form submission (when mode='server').
   * @public
   */
  @property({ type: String })
  action?: string;

  /**
   * Target window/frame for form submission.
   * @public
   */
  @property({ type: String })
  target?: string;

  /**
   * Form encoding type.
   * @public
   */
  @property({ type: String })
  enctype?: SkyFormEnctype;

  /**
   * HTML autocomplete attribute.
   * @public
   */
  @property({ type: String })
  autocomplete?: SkyFormAutocomplete;

  /**
   * When true, disables native HTML5 validation.
   * @public
   */
  @property({ type: Boolean })
  noValidate = false;

  /**
   * When true, pressing Enter in any input submits the form (except textarea).
   * @public
   */
  @property({ type: Boolean })
  enterSubmit = false;

  /**
   * When true, resets the form after successful submission.
   * @public
   */
  @property({ type: Boolean })
  resetOnSubmit = false;

  /**
   * CSRF token for form submission protection.
   * @public
   */
  @property({ type: String })
  csrfToken: string | null = null;

  /**
 * Array of current validation error messages.
 * @private
 */
  @state()
  private validationErrors: string[] = [];

  /**
   * Indicates whether the last form submission was successful.
   * @private
   */
  @state()
  private submitSuccess = false;

  /**
 * Indicates whether validation is currently active (errors shown).
 * @private
 */
  @state()
  private validationActive = false;



  /** Reference to the native form element. */
  @query("form")
  private _formEl!: HTMLFormElement;

  private _mo?: MutationObserver;
  private _syncQueued = false;
  private _handlingSubmit = false;
  private _handlingInvalid = false;

  /** MUST be light DOM for button[form="..."] + native id lookup */

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  protected override createRenderRoot(): this {
    return this;
  }

  /**
   * Returns the native HTML form element.
   * @public
   */
  public get formElement(): HTMLFormElement {
    return this._formEl;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this._queueSync();

    // Capture submit on the host before it reaches the inner <form> target.
    this.addEventListener("submit", this._onHostSubmitCapture as any, true);
    this.addEventListener("formdata", this._onFormDataCapture as any, true);
    this.addEventListener("value-changed", this._handleValueChanged as any);
    this.addEventListener("keydown", this._onEnterKey as any, true);
    this.addEventListener("invalid", this._onInvalidCapture as any, true);
    this._mo = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) => {
        for (const node of m.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) return true;
        }
        for (const node of m.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) return true;
        }
        return false;
      });
      if (relevant) this._queueSync(false);
    });
    this._mo.observe(this, { childList: true, subtree: true });
  }

  override disconnectedCallback(): void {
    this.removeEventListener("submit", this._onHostSubmitCapture as any, true);
    this.removeEventListener("formdata", this._onFormDataCapture as any, true);
    this.removeEventListener("value-changed", this._handleValueChanged as any);
    this.removeEventListener("keydown", this._onEnterKey as any, true);
    this.removeEventListener("invalid", this._onInvalidCapture as any, true);
    this._mo?.disconnect();
    super.disconnectedCallback();
  }

  private _queueSync(revalidate = false): void {
    if (this._syncQueued) return;
    this._syncQueued = true;
    const shouldRevalidate = revalidate;

    queueMicrotask(() => {
      this._syncQueued = false;
      this._moveHostIdToNativeForm();
      this._ensureNativeFormHasId();
      this._assignFormOwnerToChildren();
      this._refreshAggregates(shouldRevalidate);
    });
  }

  private _onEnterKey = (e: KeyboardEvent) => {
    if (!this.enterSubmit) return;
    if (e.key !== "Enter") return;

    const target = e.target as HTMLElement;

    // Allow Enter inside textarea.
    if (target instanceof HTMLTextAreaElement) return;

    if (target.closest('button[type="submit"], input[type="submit"]')) {
      return;
    }

    e.preventDefault();
    this.requestSubmit();
  };

  /**
   * Make `document.getElementById(hostId)` return the native form.
   * Copies host id => native form id and removes id from host to avoid duplicate ids.
   */
  private _moveHostIdToNativeForm() {
    const hostId = this.id?.trim();
    if (!hostId || !this._formEl) return;

    // set native form id
    if (this._formEl.id !== hostId) this._formEl.id = hostId;

    // remove host id so DOM lookups hit <form>
    if (this.id === hostId) this.removeAttribute("id");
  }

  /**
   * Slotted controls remain in the light DOM under `<sky-form>`, not under the inner `<form>`.
   * `_assignFormOwnerToChildren` needs a stable `id` on that `<form>` to set `form="…"` on
   * associated controls. When the host has no `id`, generate one so association still works.
   */
  private _ensureNativeFormHasId(): void {
    if (!this._formEl?.id) {
      const suffix =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      this._formEl.id = `sky-form-${suffix}`;
    }
  }

  private _handleValueChanged = (e: Event) => {
    if (!this.liveValidation) return;

    // Only process if it's coming from our form elements
    const target = e.target as HTMLElement;
    if (
      !target.matches(
        "sky-input, sky-combobox, sky-datepicker, sky-checkbox, sky-radio-group, sky-otp, sky-serial, sky-counter, sky-switch, input, select, textarea",
      )
    ) {
      return;
    }

    // Queue a sync to update validation errors
    const sky = target as { validateInput?: () => unknown };
    if (typeof sky.validateInput === "function") {
      sky.validateInput();
    }
    this._refreshAggregates(false);

    // Emit form-change event with current form data
    this._emitFormChange();
  };

  /**
   * Whether the form passes constraint validation, without calling `form.checkValidity()`.
   * `HTMLFormElement.checkValidity()` dispatches `invalid` on each invalid control; during
   * liveValidation that runs on every keystroke and our capture listener would focus the first
   * invalid field, stealing focus from the control the user is editing.
   */
  private _formValidQuiet(): boolean {
    const form = this._formEl;
    if (!form) return true;

    for (const el of Array.from(form.elements)) {
      const h = el as HTMLElement & {
        willValidate?: boolean;
        validity?: ValidityState;
      };
      if (!("validity" in h) || !h.validity) continue;
      if ("willValidate" in h && h.willValidate === false) continue;
      if (!h.validity.valid) return false;
    }
    return true;
  }

  private _emitFormChange(): void {
    if (!this._formEl) return;

    const fd = new FormData(this._formEl);
    const json = this._formDataToJSON(fd);

    this.dispatchEvent(
      new CustomEvent<SkyFormChangeDetail>("form-change", {
        detail: {
          values: json,
          formData: fd,
          success: this._formValidQuiet(),
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Assigns form ownership to elements not inside the native <form> element.
   * Ensures browser association for native submit + validation + FormData.
   */
  private _assignFormOwnerToChildren() {
    const formId = this._formEl?.id;
    if (!formId) return;

    const candidates = Array.from(
      this.querySelectorAll<HTMLElement>(
        [
          "input",
          "select",
          "textarea",
          "button",
          "sky-button",
          "sky-input",
          "sky-combobox",
          "sky-datepicker",
          "sky-checkbox",
          "sky-radio-group",
          "sky-otp",
          "sky-serial",
          "sky-counter",
          "sky-switch",
        ].join(","),
      ),
    );

    for (const el of candidates) {
      // already inside the real form = skip
      if (el.closest("form") === this._formEl) continue;

      // assign ownership
      if ((el as { form?: HTMLFormElement }).form?.id !== formId) el.setAttribute("form", formId);
    }
  }

  private _getSkyInputs() {
    return Array.from(
      this.querySelectorAll<any>(
        "sky-input, sky-combobox, sky-datepicker, sky-checkbox, sky-radio-group, sky-otp, sky-serial, sky-counter, sky-switch",
      ),
    );
  }

  private _emitValidationErrors(): void {
    this.dispatchEvent(
      new CustomEvent<SkyFormValidationErrorsDetail>("validation-errors", {
        detail: { errors: [...this.validationErrors] },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onFormDataCapture = (e: Event) => {
    // Only intercept native form's event
    if (!(e.target instanceof HTMLFormElement)) return;
    if (e.target !== this._formEl) return;

    // Stop native bubbling so Vue does not handle the inner form event.
    e.stopImmediatePropagation();
    // formdata is informational; do not preventDefault.
  };

  private _onInvalidCapture = (e: Event) => {
    const t = e.target as any;

    // only react to invalids belonging to THIS form
    if (t?.form && t.form !== this._formEl) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    // invalid handler activates validation and focuses the first invalid field.
    this._activateValidation();

    this._focusFirstInvalid();
  };

  private _refreshAggregates(revalidate = true) {
    const errs: string[] = [];

    // 1) sky-* component errors (if they provide them)
    const skyInputs = this._getSkyInputs();
    for (const input of skyInputs) {
      // Skip validateInput during invalid-event recursion and on mutation/ownership sync.
      if (
        revalidate &&
        !this._handlingInvalid &&
        typeof input.validateInput === "function"
      ) {
        input.validateInput();
      }

      if (Array.isArray(input.validationErrors)) {
        errs.push(...input.validationErrors);
      }
    }

    // 2) native constraint validation messages from associated elements
    const form = this._formEl;
    if (form) {
      const elements = Array.from(form.elements) as any[];
      for (const el of elements) {
        // Skip buttons etc
        if (!("willValidate" in el) || !el.willValidate) continue;

        const isValid =
          "validity" in el && el.validity ? el.validity.valid : true;

        if (!isValid) {
          const msg =
            el.validationMessage || `${el.name || el.id || "Field"} is invalid`;
          errs.push(msg);
        }
      }
    }

    const next = [...new Set(errs)];
    if (next.join("|") !== this.validationErrors.join("|")) {
      this.validationErrors = next;
      this._emitValidationErrors();
    }
  }

  private _focusFirstInvalid() {
    const inputs = this._getSkyInputs();
    const firstInvalid = inputs.find(input => input.invalid === true);

    if (firstInvalid) {
      this._focusElement(firstInvalid);
    }
  }

  private _focusElement(element: HTMLElement) {
    const prefersReducedMotion =
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;
    element.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
    });
    setTimeout(() => {
      element.focus({ preventScroll: true });
    }, 100);
  }

  private _syncNow() {
    this._moveHostIdToNativeForm();
    this._ensureNativeFormHasId();
    this._assignFormOwnerToChildren();
    this._refreshAggregates();
  }

  private _onHostSubmitCapture = (e: Event) => {
    // only intercept submits coming from the inner native form
    if (!(e.target instanceof HTMLFormElement)) return;
    if (e.target !== this._formEl) return;

    // avoid recursion when we re-dispatch
    if (this._handlingSubmit) return;
    this._handlingSubmit = true;

    try {
      this._syncNow();
      const ok = this._formEl.checkValidity();
      if (!ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.submitSuccess = false;
        this._focusFirstInvalid();
        return;
      }

      // Server mode: allow native form submission (page refresh/navigation).
      if (this.mode === "server") {
        this.submitSuccess = true;
        if (this.resetOnSubmit) {
          queueMicrotask(() => this.reset());
        }
        return;
      }

      // Always prevent default for client mode to avoid page refresh
      e.preventDefault();
      e.stopImmediatePropagation();

      const fd = new FormData(this._formEl);

      // Emit a real FormDataEvent when available (Vue handlers use e.formData).
      let dispatched = false;
      try {
        // @ts-ignore
        const fde = new FormDataEvent("formdata", {
          formData: fd,
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(fde);
        dispatched = true;
      } catch { }

      // fallback for older browsers
      if (!dispatched) {
        this.dispatchEvent(
          new CustomEvent<SkyFormFormDataFallbackDetail>("formdata", {
            detail: { formData: fd },
            bubbles: true,
            composed: true,
          }),
        );
      }

      // optional perk event (JSON)
      const json = this._formDataToJSON(fd);
      this.dispatchEvent(
        new CustomEvent<SkyFormSubmitDetail>("form-submit", {
          detail: {
            values: json,
            formData: fd,
            success: this._formEl.checkValidity(),
          },
          bubbles: true,
          composed: true,
        }),
      );

      this.submitSuccess = true;
      if (this.resetOnSubmit) {
        queueMicrotask(() => this.reset());
      }
    } finally {
      this._handlingSubmit = false;
    }
  };

  private _formDataToJSON(fd: FormData): SkyFormValues {
    const obj: SkyFormValues = {};

    fd.forEach((value, key) => {
      if (key in obj) {
        // convert to array if duplicate keys
        if (Array.isArray(obj[key])) {
          obj[key].push(value);
        } else {
          obj[key] = [obj[key], value];
        }
      } else {
        obj[key] = value;
      }
    });

    return obj;
  }

  /** Activates validation mode and refreshes error aggregates. */
  private _activateValidation(): void {
    this.validationActive = true;

    const skyInputs = this._getSkyInputs();
    for (const input of skyInputs) {
      input.validationActive = true;
      input.showErrors = true;
    }

    // Defer aggregation until validationActive is set on child controls.
    queueMicrotask(() => this._refreshAggregates());
  }

  /**
   * Checks form validity without displaying validation messages.
   * @returns {boolean} True if the form is valid, false otherwise.
   * @public
   */
  public checkValidity(): boolean {
    // Sync synchronously: child controls must have their `form` association set
    // before form.checkValidity() runs, otherwise they are excluded from
    // form.elements and validity would be reported incorrectly.
    this._syncNow();
    return this._formEl?.checkValidity?.() ?? true;
  }

  /**
   * Checks form validity and displays validation messages.
   * @returns {boolean} True if the form is valid, false otherwise.
   * @public
   */
  public reportValidity(): boolean {
    this._syncNow();
    return this._formEl?.reportValidity?.() ?? true;
  }

  /**
   * Requests form submission (compatible with native requestSubmit).
   * @param {HTMLElement} submitter - Optional submitter element.
   * @public
   */
  public requestSubmit(submitter?: HTMLElement): void {
    this._syncNow();
    this._activateValidation();
    // @ts-ignore
    this._formEl?.requestSubmit?.(submitter);
  }

  /**
   * Resets the form to its initial state.
   * @public
   */
  public reset(): void {
    this._queueSync();
    this._formEl?.reset();
  }

  /**
   * Resets the form to its initial state (alias for reset()).
   * @public
   */
  public resetForm(): void {
    this._queueSync();
    this._formEl?.reset();
  }

  /**
   * Manually submits the form, respecting the current mode setting.
   * In client mode, triggers the client-side submission flow.
   * In server mode, performs native form submission.
   * @public
   */
  public submit(): void {
    this._activateValidation();
    if (this.mode === "client") {
      // Trigger the client-side submission flow
      const submitEvent = new SubmitEvent("submit", {
        bubbles: true,
        cancelable: true,
      });
      this._formEl?.dispatchEvent(submitEvent);
    } else {
      // In server mode, use native form submission
      this._formEl?.submit();
    }
  }

  override render() {
    return html`
      <style>
        /* Scoped to the sky-form tag: this is a light-DOM component, so
           unscoped class selectors would leak into the whole document. */
        sky-form .error-wrapper {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        sky-form .error-container {
          margin: var(--sky-space-3, 12px) 0;
          padding: var(--sky-space-2, 8px) var(--sky-space-3, 12px);
          width: 100%;
          border-radius: var(--sky-radius-secondary);
          border: 1px solid var(--sky-danger-primary);
          background: var(--sky-glass-solid);
          box-shadow: var(--sky-box-shadow-primary);
          position: relative;
          z-index: inherit;
        }
        sky-form .error-title {
          font-size: var(--sky-font-size-sm, 14px);
          color: var(--sky-danger-primary);
          width: 100%;
          display: flex;
          justify-content: center;
        }
        sky-form .error-list {
          list-style-position: inside;
          padding: var(--sky-space-1, 4px);
          margin: 0;
          text-align: start;
        }
        sky-form .error-item {
          font-size: var(--sky-font-size-xs, 12px);
          color: var(--sky-text-primary);
        }
      </style>

      <form
        part="form"
        method=${ifDefined(this.method)}
        action=${ifDefined(this.action)}
        enctype=${ifDefined(this.enctype)}
        autocomplete=${ifDefined(this.autocomplete)}
        ?novalidate=${this.noValidate}
        target=${ifDefined(this.target || undefined)}
      >
        ${this.csrfToken
        ? html`<input
              type="hidden"
              name="csrfmiddlewaretoken"
              .value=${this.csrfToken}
            />`
        : nothing}
        <slot></slot>
      </form>

      ${this.errorBox &&
        this.validationErrors.length > 0 &&
        this.showErrors &&
        this.validationActive
        ? html`
            <div class="error-wrapper" part="error-box">
              <div class="error-container">
                <div class="error-title">${this.errorBoxMessage}</div>
                <ul class="error-list" part="error-list">
                  ${this.validationErrors.map(
          (err) => html`<li class="error-item">${err}</li>`,
        )}
                </ul>
              </div>
            </div>
          `
        : nothing}
    `;
  }
}
